import { useState } from 'react';
import { Search, BookOpen, FlaskConical, Bell, BellOff, ExternalLink, Calendar, MapPin, Building2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface PubMedArticle {
  pmid: string;
  title: string;
  authors: string[];
  journal: string;
  pubDate: string;
  link: string;
}

interface ClinicalTrial {
  nctId: string;
  title: string;
  phase: string[];
  status: string;
  locations: Array<{ city?: string; country?: string }>;
  sponsor: string;
  link: string;
}

interface ResearchResults {
  pubmedStudies: PubMedArticle[];
  clinicalTrials: ClinicalTrial[];
  condition: string;
  generatedAt: string;
  cached?: boolean;
  cachedAt?: string;
}

export default function ResearchPage() {
  const { accessToken } = useAuth();
  const [condition, setCondition] = useState('');
  const [results, setResults] = useState<ResearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscribed, setSubscribed] = useState(false);

  const searchResearch = async () => {
    if (!condition.trim()) {
      setError('Te rog introdu o condiție medicală');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get token from localStorage directly to avoid stale state
      const token = localStorage.getItem('accessToken') || accessToken;
      
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/research/search`,
        {
          params: { condition },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResults(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la căutarea cercetărilor');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!condition.trim()) return;

    try {
      // Get token from localStorage directly to avoid stale state
      const token = localStorage.getItem('accessToken') || accessToken;
      
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/research/subscribe`,
        { condition },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubscribed(true);
      alert('Vei primi alerte despre noutăți pentru această condiție!');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la abonare');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Cercetare Medicală" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchResearch()}
                placeholder="Ex: dermatomiozită, lupus, artrită reumatoidă..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={searchResearch}
              disabled={loading}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2 whitespace-nowrap"
            >
              <Search size={20} />
              {loading ? 'Căutare...' : 'Caută'}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Results */}
        {results && (
          <>
            {/* Header cu info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-blue-900">
                  Rezultate pentru: <span className="capitalize">{results.condition}</span>
                </h2>
                <p className="text-sm text-blue-700">
                  {results.cached ? `Date din cache (${new Date(results.cachedAt!).toLocaleDateString()})` : 'Date proaspete'}
                </p>
              </div>
              <button
                onClick={handleSubscribe}
                disabled={subscribed}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {subscribed ? <BellOff size={18} /> : <Bell size={18} />}
                {subscribed ? 'Abonat' : 'Primește alerte'}
              </button>
            </div>

            {/* PubMed Studies */}
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="text-green-600" size={24} />
                <h3 className="text-xl font-bold">Studii Recente (PubMed)</h3>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                  {results.pubmedStudies.length}
                </span>
              </div>

              {results.pubmedStudies.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                  Niciun studiu recent găsit pentru această condiție
                </div>
              ) : (
                <div className="space-y-4">
                  {results.pubmedStudies.map((article) => (
                    <div key={article.pmid} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                      <h4 className="font-semibold text-lg mb-2">{article.title}</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        {article.authors.slice(0, 3).join(', ')}
                        {article.authors.length > 3 && ` +${article.authors.length - 3} autori`}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar size={16} />
                          {article.pubDate}
                        </span>
                        <span>•</span>
                        <span>{article.journal}</span>
                      </div>
                      <a
                        href={article.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Vezi pe PubMed
                        <ExternalLink size={16} />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Clinical Trials */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <FlaskConical className="text-purple-600" size={24} />
                <h3 className="text-xl font-bold">Studii în Curs (ClinicalTrials.gov)</h3>
                <span className="px-2 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                  {results.clinicalTrials.length}
                </span>
              </div>

              {results.clinicalTrials.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                  Niciun test în recrutare găsit pentru această condiție
                </div>
              ) : (
                <div className="space-y-4">
                  {results.clinicalTrials.map((trial) => (
                    <div key={trial.nctId} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-lg flex-1">{trial.title}</h4>
                        <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full whitespace-nowrap ml-4">
                          {trial.phase.join(', ') || 'N/A'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <span className="font-medium">Status:</span>
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded">{trial.status}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Building2 size={16} />
                          <span>{trial.sponsor}</span>
                        </div>
                      </div>

                      {trial.locations.length > 0 && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                          <MapPin size={16} />
                          <span>
                            {trial.locations.map((loc, idx) => (
                              <span key={idx}>
                                {loc.city && loc.country && `${loc.city}, ${loc.country}`}
                                {idx < trial.locations.length - 1 && ' • '}
                              </span>
                            ))}
                          </span>
                        </div>
                      )}

                      <a
                        href={trial.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 font-medium"
                      >
                        Vezi detalii complete ({trial.nctId})
                        <ExternalLink size={16} />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {/* Empty State */}
        {!results && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FlaskConical className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Descoperă cele mai recente cercetări medicale
            </h3>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Caută studii clinice recente (PubMed) și teste în curs (ClinicalTrials.gov) pentru
              dermatomiozită, lupus, artrită și alte condiții autoimune.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
